import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { Button } from '../ui/Button';
import type { DraftRateConfig } from '../../api/gameConfig';
import type { ActiveStrategyDto } from '../../types/strategy';

interface DraftSettingsPanelProps {
  draftRate: string;
  draftTarget: string;
  draftRates: DraftRateConfig[];
  maxDraftTarget: number;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  onDraftRateChange: (value: string) => void;
  onDraftTargetChange: (value: string) => void;
  onSave: () => void;
  activeStrategy: ActiveStrategyDto | null;
  onLoadFromStrategy: () => void;
  draftLoadMessage: string | null;
  /** SF-141: stacked layout (chip → Rate select → Target/Save row) for the mobile Draft tab —
   *  desktop's existing single-row `flex items-end gap-4` layout is unchanged when omitted. */
  compact?: boolean;
}

const selectStyle = {
  ...INPUT_STYLE,
  border: `1px solid ${COLORS.inputBorder}`,
} as const;

const compactTargetInputStyle = {
  ...selectStyle,
  fontSize: 16,
  minHeight: 44,
} as const;

function LoadFromStrategyChip({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded whitespace-nowrap"
      style={{
        backgroundColor: COLORS.sectionHeader,
        border: `1px solid ${COLORS.border}`,
        color: '#B89878',
        cursor: 'pointer',
      }}
    >
      ⚡ Load from Strategy
    </button>
  );
}

function Feedback({ draftLoadMessage, error, success }: { draftLoadMessage: string | null; error: string | null; success: string | null }) {
  return (
    <>
      {draftLoadMessage && (
        <div className="text-xs" style={{ color: COLORS.text.secondary }}>{draftLoadMessage}</div>
      )}
      {error && (
        <div className="text-sm" style={{ color: COLORS.negative }}>{error}</div>
      )}
      {success && (
        <div className="text-sm" style={{ color: COLORS.positive }}>{success}</div>
      )}
    </>
  );
}

export function DraftSettingsPanel({
  draftRate,
  draftTarget,
  draftRates,
  maxDraftTarget,
  isSubmitting,
  error,
  success,
  onDraftRateChange,
  onDraftTargetChange,
  onSave,
  activeStrategy,
  onLoadFromStrategy,
  draftLoadMessage,
  compact = false,
}: DraftSettingsPanelProps) {
  const Heading = compact ? 'h3' : 'div';

  const chip = activeStrategy?.settings && <LoadFromStrategyChip onClick={onLoadFromStrategy} />;

  const rateField = (
    <div className={compact ? undefined : 'flex-1'}>
      <label htmlFor="draft-rate-select" className="block text-xs mb-1" style={{ color: COLORS.text.tertiary }}>
        Draft Rate
      </label>
      <select
        id="draft-rate-select"
        value={draftRate}
        onChange={(e) => onDraftRateChange(e.target.value)}
        className="w-full rounded text-sm py-1.5 px-2"
        style={selectStyle}
      >
        {draftRates.map((rate) => (
          <option key={rate.id} value={rate.id}>
            {rate.label} ({rate.speedPercent}% · {rate.costPerSoldier} gc/soldier)
          </option>
        ))}
      </select>
    </div>
  );

  const targetField = (
    <div className={compact ? undefined : 'w-24'}>
      <label htmlFor="draft-target-input" className="block text-xs mb-1" style={{ color: COLORS.text.tertiary }}>
        Target (%)
      </label>
      <input
        id="draft-target-input"
        type="number"
        min="0"
        max={maxDraftTarget}
        value={draftTarget}
        onChange={(e) => onDraftTargetChange(e.target.value)}
        className={compact ? 'w-full rounded px-2 text-right' : 'w-full rounded text-sm py-1.5 px-2 text-right'}
        style={compact ? compactTargetInputStyle : selectStyle}
      />
    </div>
  );

  const saveButton = (
    <Button onClick={onSave} isLoading={isSubmitting}>
      Save
    </Button>
  );

  return (
    <div
      className="rounded p-4 space-y-3"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <Heading className="text-sm font-semibold" style={{ color: COLORS.text.primary }}>
        Draft Settings
      </Heading>

      {compact ? (
        <>
          {chip}
          {rateField}
          <div className="grid gap-3 items-end" style={{ gridTemplateColumns: '1fr 88px' }}>
            {targetField}
            {saveButton}
          </div>
        </>
      ) : (
        <div className="flex items-end gap-4">
          {rateField}
          {targetField}
          {chip}
          {saveButton}
        </div>
      )}

      <Feedback draftLoadMessage={draftLoadMessage} error={error} success={success} />
    </div>
  );
}
